import { createSlice } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type {
  GetTransmittalDataByIdResponse,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Response,
} from "@ServerTypes/transmittals/transmittals.schema";

type TransmittalDetailsState = NonNullable<GetTransmittalDataByIdResponse["data"]> | Record<string, never>;
type TransmittalMetadataState = NonNullable<GetTransmittalDataByIdResponse["metadata"]> | Record<string, never>;
type TransmittalAccessState = IsTransmittal403Response;
type TransmittalVersionsState = GetTransmittalFileVersionsForSpaceResponse;

const saveTransmitalSlice = createSlice({
  name: "transmittals",
  initialState: {} as TransmittalDetailsState,
  reducers: {
    setTransmitalsData: (_state, action) => action.payload,
    mergeTransmitalsData: (state, action) => ({ ...state, ...action.payload }),
  },
});

const transmittalMetadataSlice = createSlice({
  name: "transmittalMetadata",
  initialState: {} as TransmittalMetadataState,
  reducers: {
    setTransmitalMetadata: (_state, action) => action.payload,
  },
});

const transmittalAccessSlice = createSlice({
  name: "transmittalAccess",
  initialState: { status: false, canViewTransmittal: false } as TransmittalAccessState,
  reducers: {
    setTransmittalAccess: (_state, action) => action.payload,
  },
});

const transmittalDataForReviewsSlice = createSlice({
  name: "transmittalDataForReviews",
  initialState: null,
  reducers: {
    setTransmittalDataForReviews: (_state, action) => action.payload,
  },
});

const transmittalReferencesSlice = createSlice({
  name: "transmittalReferences",
  initialState: [],
  reducers: {
    setTransmittalReferences: (_state, action) => action.payload,
  },
});

const transmittalVersionsSlice = createSlice({
  name: "transmittalVersions",
  initialState: [] as TransmittalVersionsState,
  reducers: {
    setTransmittalVersions: (_state, action) => action.payload,
  },
});

// `setTransmitalsData`, `mergeTransmitalsData`, `setTransmitalMetadata`,
// `setTransmittalDataForReviews`, `setTransmittalReferences`,
// `setTransmittalVersions` are exported raw — payloads are either unions
// containing `Record<string, never>` (index-signature), `null`, arrays, or
// untyped (the reducers do not declare typed payloads).
export const { setTransmitalsData, mergeTransmitalsData } = saveTransmitalSlice.actions;
export const { setTransmitalMetadata } = transmittalMetadataSlice.actions;
export const { setTransmittalDataForReviews } = transmittalDataForReviewsSlice.actions;
export const { setTransmittalReferences } = transmittalReferencesSlice.actions;
export const { setTransmittalVersions } = transmittalVersionsSlice.actions;

const { setTransmittalAccess: _setTransmittalAccessRaw } = transmittalAccessSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setTransmittalAccess = <T extends Exact<TransmittalAccessState, T>>(payload: T) =>
  _setTransmittalAccessRaw(payload);

export const saveTransmital = saveTransmitalSlice.reducer;
export const saveTransmitalMetadata = transmittalMetadataSlice.reducer;
export const setTransmittalAccessReducer = transmittalAccessSlice.reducer;
export const setTransmittalDataForReviewsReducer = transmittalDataForReviewsSlice.reducer;
export const setTransmittalReferencesReducer = transmittalReferencesSlice.reducer;
export const setTransmittalVersionsReducer = transmittalVersionsSlice.reducer;
